import { parseUnits } from '@ethersproject/units';
import { ONE_HUNDRED_PERCENT } from 'app/constants';
import { shiftDecimal } from 'app/functions/silo/calculations';
import { BigNumberWithDecimals, PERCENT_DECIMALS } from '@silo/types';

export const validScreens = ['deposit', 'borrow', 'withdraw', 'repay', 'charts'] as const;

export type Screen = (typeof validScreens)[number];

export type SetActiveTab = (value: Screen) => void;

/**
 * Percentage that will be subtracted from any LTV limits
 *
 * 0.0025 / 1 aka 0.25%
 */
const LTV_BUFFER = parseUnits('0.0025', PERCENT_DECIMALS);
const MULTIPLIER = ONE_HUNDRED_PERCENT.sub(LTV_BUFFER);

/**
 * Reduces an LTV limit by a very small percentage. This generally prevents the
 * transaction from being invalidated or rejected due to block-by-block
 * fluctuations in asset prices.
 *
 * Only applicable for borrowing and withdrawing, as these are the only actions
 * that can increase a user's LTV.
 *
 * ```ts
 * adjustLtvLimit({ bigNumber: BigNumber.from('100'), decimals: 18 }) // => '99.75' as a BigNumberWithDecimals
 * ```
 */
export const adjustLtvLimit = ({ bigNumber, decimals }: BigNumberWithDecimals) => {
  const newLtvLimit = shiftDecimal(bigNumber.mul(MULTIPLIER), -PERCENT_DECIMALS);

  return {
    bigNumber: newLtvLimit,
    decimals,
  };
};
